import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, X } from 'lucide-react';
import Footer from '@/components/Footer';
import PublicNav from '@/components/PublicNav';
import '@/styles/website.css';

// Australian cities with coordinates (kept for the location filter wiring)
const AUSTRALIAN_CITIES = [
  { name: 'Sydney', lat: -33.8688, lon: 151.2093 },
  { name: 'Melbourne', lat: -37.8136, lon: 144.9631 },
  { name: 'Brisbane', lat: -27.4698, lon: 153.0251 },
  { name: 'Perth', lat: -31.9505, lon: 115.8605 },
  { name: 'Adelaide', lat: -34.9285, lon: 138.6007 },
  { name: 'Hobart', lat: -42.8821, lon: 147.3272 },
  { name: 'Darwin', lat: -12.4634, lon: 130.8456 },
  { name: 'Canberra', lat: -35.2809, lon: 149.1300 },
];

// Filter chips → existing skillFilter param (API does a case-insensitive
// regex match against each crew member's `skills` array).
const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'photographer', label: 'Photography' },
  { key: 'videographer', label: 'Video' },
  { key: 'editor', label: 'Editing' },
  { key: 'drone', label: 'Drone' },
];

// Brand-gradient covers + matching avatar fills (TOKENS.md).
const COVER_GRADIENTS = [
  { cover: 'linear-gradient(135deg,#572B7C,#0C1120)', avatar: '#572B7C' },
  { cover: 'linear-gradient(135deg,#78287C,#28C0D0)', avatar: '#78287C' },
  { cover: 'linear-gradient(135deg,#67C6B4,#572B7C)', avatar: '#67C6B4' },
  { cover: 'linear-gradient(135deg,#0C1120,#28C0D0)', avatar: '#28C0D0' },
  { cover: 'linear-gradient(135deg,#28C0D0,#78287C)', avatar: '#28C0D0' },
  { cover: 'linear-gradient(135deg,#572B7C,#78287C)', avatar: '#572B7C' },
];

const gradientFor = (id) => {
  const str = String(id || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return COVER_GRADIENTS[hash % COVER_GRADIENTS.length];
};

const initialsOf = (name) =>
  String(name || 'Crew')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

// Derive a human role + display tags from the raw skills array.
const titleCase = (s) =>
  String(s || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const deriveRole = (skills) => {
  if (Array.isArray(skills) && skills.length) return skills.map(titleCase).slice(0, 2).join(' & ');
  return 'Photo & Video Crew';
};

const derivePrimary = (skills) => {
  if (Array.isArray(skills) && skills.length) return titleCase(skills[0]);
  return 'Crew';
};

const deriveTags = (skills) =>
  Array.isArray(skills) ? skills.slice(0, 3).map(titleCase) : [];

const CrewDirectory = () => {
  const navigate = useNavigate();
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Location filter state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  // Skill filter state (driven by the chips)
  const [skillFilter, setSkillFilter] = useState('all');

  // Client-side text search over loaded crew (name + role + city)
  const [q, setQ] = useState('');

  // Profile modal state
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);

  const handleCardClick = async (member) => {
    setSelectedMember(member);
    setMemberLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/${member.id}`
      );
      if (response.data.success) {
        setFullProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setMemberLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedMember(null);
    setFullProfile(null);
  };

  // Fetch crew when location or skill filter changes
  useEffect(() => {
    setCrew([]);
    setPage(1);
    fetchCrew(1, true);
  }, [selectedLocation, skillFilter]);

  const fetchCrew = async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);

      let url = `${process.env.REACT_APP_BACKEND_URL}/api/public/crew-directory?page=${pageNum}&limit=30`;

      if (selectedLocation) {
        url += `&location_lat=${selectedLocation.lat}&location_lon=${selectedLocation.lon}&radius_km=100`;
      }

      if (skillFilter && skillFilter !== 'all') {
        url += `&skill=${skillFilter}`;
      }

      const response = await axios.get(url);

      if (response.data.success) {
        const newCrew = (response.data.crew || []).filter(
          member => member.profile_photo_url
        );

        if (reset || pageNum === 1) {
          setCrew(newCrew);
        } else {
          setCrew(prev => [...prev, ...newCrew]);
        }

        setTotal(response.data.total);
        setHasMore(response.data.has_more);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch crew:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCitySelect = (city) => {
    if (city === null) {
      setSelectedCity(null);
      setSelectedLocation(null);
    } else {
      setSelectedCity(city.name);
      setSelectedLocation({ lat: city.lat, lon: city.lon, name: city.name });
    }
  };

  // Client-side text search applied on top of the API results.
  const visibleCrew = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return crew;
    return crew.filter((m) => {
      const role = deriveRole(m.skills);
      const haystack = `${m.first_name || ''} ${role} ${m.location || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [crew, q]);

  const countLabel = `${visibleCrew.length} ${
    visibleCrew.length === 1 ? 'professional' : 'professionals'
  }${selectedLocation ? ` near ${selectedLocation.name}` : ' across Australia'}`;

  return (
    <div className="wz-root" style={{ background: '#fff', overflowX: 'hidden' }}>
      {/* Responsive rules (media queries can't be inline) */}
      <style>{`
        @media (max-width: 1100px) {
          .wz-crew-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 900px) {
          .wz-crew-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 620px) {
          .wz-crew-grid { grid-template-columns: 1fr !important; }
          .wz-filter-row { flex-direction: column !important; align-items: stretch !important; }
        }
      `}</style>

      <PublicNav />

      {/* ============ HERO ============ */}
      <header style={{ position: 'relative', background: 'var(--wz-ink)', overflow: 'hidden' }}>
        {/* drifting gradient backdrop */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div
            className="wz-blob wz-drift1"
            style={{
              width: 760, height: 600, top: -220, right: -160,
              background: 'radial-gradient(closest-side, rgba(120,40,124,.55), transparent 72%)',
            }}
          />
          <div
            className="wz-blob wz-drift2"
            style={{
              width: 640, height: 520, bottom: -260, left: -180,
              background: 'radial-gradient(closest-side, rgba(40,192,208,.26), transparent 72%)',
            }}
          />
          <div
            className="wz-blob wz-drift3"
            style={{
              width: 520, height: 460, top: '40%', left: '38%',
              background: 'radial-gradient(closest-side, rgba(87,43,124,.4), transparent 72%)',
            }}
          />
        </div>

        <div
          style={{
            position: 'relative', maxWidth: 1180, margin: '0 auto',
            padding: '110px 40px 0',
          }}
        >
          <div className="wz-eyebrow" style={{ color: 'var(--wz-aqua)' }}>The Zu Crew</div>
          <h1
            className="wz-h1"
            style={{
              fontWeight: 800, fontSize: 'clamp(38px,5vw,64px)', lineHeight: 1,
              letterSpacing: '-1.8px', color: '#fff', margin: '16px 0 0',
            }}
          >
            Meet the crew.
          </h1>
          <p
            style={{
              fontSize: 19, lineHeight: 1.6, color: 'rgba(255,255,255,.74)',
              maxWidth: '54ch', margin: '20px 0 0',
            }}
          >
            Every member is quality-checked and ABN-verified — real photo &amp; video professionals
            across Australia, ready for your next shoot.
          </p>

          {/* search + filters */}
          <div
            className="wz-filter-row"
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              marginTop: 34, flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
                borderRadius: 999, padding: '13px 20px', flex: 1, minWidth: 260, maxWidth: 420,
              }}
            >
              <span style={{ fontSize: 16, color: 'var(--wz-slate400)' }}>🔍</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, role or city"
                style={{
                  border: 'none', outline: 'none', fontFamily: '"Work Sans", sans-serif',
                  fontSize: 15, color: 'var(--wz-ink)', background: 'transparent', width: '100%',
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {FILTER_CHIPS.map((chip) => {
                const active = skillFilter === chip.key;
                return (
                  <button
                    key={chip.key}
                    onClick={() => setSkillFilter(chip.key)}
                    style={{
                      fontFamily: '"Work Sans", sans-serif', fontSize: 13.5, fontWeight: 600,
                      borderRadius: 999, padding: '10px 16px', cursor: 'pointer',
                      transition: 'background .15s, color .15s',
                      background: active ? '#fff' : 'transparent',
                      color: active ? 'var(--wz-ink)' : 'rgba(255,255,255,.7)',
                      border: active
                        ? '1px solid #fff'
                        : '1px solid rgba(255,255,255,.22)',
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Optional city quick-filter row (drives the existing location filter) */}
          <div
            style={{
              display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
              marginTop: 14,
            }}
          >
            <button
              onClick={() => handleCitySelect(null)}
              style={{
                fontFamily: '"Work Sans", sans-serif', fontSize: 12.5, fontWeight: 600,
                borderRadius: 999, padding: '7px 13px', cursor: 'pointer',
                transition: 'background .15s, color .15s',
                background: !selectedCity ? 'var(--wz-aqua)' : 'transparent',
                color: !selectedCity ? 'var(--wz-ink)' : 'rgba(255,255,255,.6)',
                border: !selectedCity ? '1px solid var(--wz-aqua)' : '1px solid rgba(255,255,255,.16)',
              }}
            >
              All Australia
            </button>
            {AUSTRALIAN_CITIES.map((city) => {
              const active = selectedCity === city.name;
              return (
                <button
                  key={city.name}
                  onClick={() => handleCitySelect(city)}
                  style={{
                    fontFamily: '"Work Sans", sans-serif', fontSize: 12.5, fontWeight: 600,
                    borderRadius: 999, padding: '7px 13px', cursor: 'pointer',
                    transition: 'background .15s, color .15s',
                    background: active ? 'var(--wz-aqua)' : 'transparent',
                    color: active ? 'var(--wz-ink)' : 'rgba(255,255,255,.6)',
                    border: active ? '1px solid var(--wz-aqua)' : '1px solid rgba(255,255,255,.16)',
                  }}
                >
                  {city.name}
                </button>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 18, paddingBottom: 30, fontSize: 13.5, color: 'rgba(255,255,255,.5)',
            }}
          >
            {loading && crew.length === 0 ? 'Loading crew…' : countLabel}
          </div>
        </div>
      </header>

      {/* ============ CREW GRID ============ */}
      <main style={{ maxWidth: 1180, margin: '0 auto', padding: '48px 40px 24px' }}>
        {loading && crew.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--wz-slate400)' }}>
            <div
              style={{
                display: 'inline-block', width: 44, height: 44, borderRadius: '50%',
                border: '3px solid var(--wz-slate100)', borderBottomColor: 'var(--wz-violet)',
                animation: 'szSpin 0.9s linear infinite',
              }}
            />
          </div>
        ) : visibleCrew.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--wz-slate400)' }}>
            <div
              className="wz-h3"
              style={{ fontWeight: 700, fontSize: 20, color: 'var(--wz-ink)', marginBottom: 8 }}
            >
              No crew match that search
            </div>
            <div style={{ fontSize: 15 }}>
              Try a different name, role or city — or clear your filters.
            </div>
          </div>
        ) : (
          <>
            <div
              className="wz-crew-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 20,
              }}
            >
              {visibleCrew.map((member) => {
                const g = gradientFor(member.id);
                const role = deriveRole(member.skills);
                const primary = derivePrimary(member.skills);
                const tags = deriveTags(member.skills);
                return (
                  <div
                    key={member.id}
                    onClick={() => handleCardClick(member)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleCardClick(member);
                    }}
                    style={{
                      border: '1px solid var(--wz-slate100)', borderRadius: 20,
                      overflow: 'hidden', background: '#fff', display: 'flex',
                      flexDirection: 'column', cursor: 'pointer',
                    }}
                  >
                    {/* 4:5 portrait — real photo over gradient + large monogram fallback */}
                    <div style={{ position: 'relative', aspectRatio: '4 / 5', background: g.cover, overflow: 'hidden' }}>
                      <span
                        style={{
                          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontFamily: '"Poppins", sans-serif', fontWeight: 800,
                          fontSize: 64, letterSpacing: 1, color: 'rgba(255,255,255,.16)', pointerEvents: 'none',
                        }}
                      >
                        {initialsOf(member.first_name)}
                      </span>
                      {(member.profile_photo_url || member.portfolio_images?.[0]) && (
                        <img
                          src={member.profile_photo_url || member.portfolio_images?.[0]}
                          alt={member.first_name}
                          loading="lazy"
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      {/* bottom scrim for badge/chip legibility */}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(12,17,32,.6), transparent 44%)', pointerEvents: 'none' }} />
                      <span
                        style={{
                          position: 'absolute', top: 12, right: 12, display: 'inline-flex',
                          alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600,
                          color: 'var(--wz-ink)', background: 'rgba(255,255,255,.92)',
                          borderRadius: 999, padding: '5px 11px', pointerEvents: 'none',
                        }}
                      >
                        ✓ Verified
                      </span>
                      <span
                        style={{
                          position: 'absolute', bottom: 12, left: 12, fontSize: 11.5,
                          fontWeight: 600, color: '#fff', background: 'rgba(12,17,32,.45)',
                          backdropFilter: 'blur(4px)', borderRadius: 999, padding: '5px 12px', pointerEvents: 'none',
                        }}
                      >
                        {primary}
                      </span>
                    </div>

                    {/* body */}
                    <div style={{ padding: '18px 20px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontFamily: '"Poppins", sans-serif', fontWeight: 600, fontSize: 16,
                              color: 'var(--wz-ink)', whiteSpace: 'nowrap', overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {member.first_name}
                          </div>
                          <div
                            style={{
                              fontSize: 13, color: 'var(--wz-slate400)', marginTop: 2, whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                            }}
                          >
                            {role}
                          </div>
                        </div>
                        <span
                          style={{
                            flex: 'none', fontSize: 13, fontWeight: 600, color: 'var(--wz-ink)',
                          }}
                        >
                          ★ 5.0
                        </span>
                      </div>

                      {member.location && (
                        <div
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            margin: '14px 0 12px', fontSize: 13.5, color: 'var(--wz-slate700)',
                          }}
                        >
                          📍 {member.location}
                          {member.distance_km !== null && member.distance_km !== undefined
                            ? ` · ${member.distance_km} km`
                            : ''}
                        </div>
                      )}

                      {tags.length > 0 && (
                        <div
                          style={{
                            display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18,
                          }}
                        >
                          {tags.map((tag, i) => (
                            <span
                              key={`${tag}-${i}`}
                              style={{
                                fontSize: 11.5, fontWeight: 600, color: 'var(--wz-violet)',
                                background: 'var(--wz-slate100)', borderRadius: 999,
                                padding: '5px 11px',
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <span
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14,
                          fontWeight: 600, color: 'var(--wz-magenta)', textDecoration: 'none',
                        }}
                      >
                        View profile →
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load more */}
            {hasMore && !q.trim() && (
              <div style={{ textAlign: 'center', marginTop: 44 }}>
                <button
                  onClick={() => fetchCrew(page + 1)}
                  disabled={loading}
                  className="wz-pill wz-pill-outline-dark"
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? 'Loading…' : 'Load more crew'}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* ============ JOIN CTA ============ */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '60px 40px 96px' }}>
        <div
          style={{
            position: 'relative', background: 'var(--wz-ink)', borderRadius: 28,
            padding: '64px 40px', textAlign: 'center', overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute', inset: 0,
              background:
                'radial-gradient(620px 380px at 50% -10%, rgba(120,40,124,.6), transparent 62%),radial-gradient(520px 360px at 82% 120%, rgba(40,192,208,.26), transparent 60%)',
            }}
          />
          <div style={{ position: 'relative' }}>
            <h2
              className="wz-h2"
              style={{
                fontWeight: 800, fontSize: 'clamp(30px,4.4vw,48px)', letterSpacing: '-1.2px',
                color: '#fff', margin: '0 auto', maxWidth: '18ch', lineHeight: 1.02,
              }}
            >
              Want to be on this page?
            </h2>
            <p
              style={{
                fontSize: 18, lineHeight: 1.6, color: 'rgba(255,255,255,.75)',
                maxWidth: '46ch', margin: '18px auto 30px',
              }}
            >
              Create your free profile, get quality-checked, and join Australia's most trusted crew
              network.
            </p>
            <button
              onClick={() => navigate('/signup')}
              className="wz-pill wz-pill-aqua"
              style={{ fontSize: 16, padding: '16px 32px' }}
            >
              Join the Zu Crew →
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* ============ PROFILE MODAL ============ */}
      {selectedMember && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(12,17,32,.7)',
            backdropFilter: 'blur(6px)', zIndex: 60, display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={handleCloseModal}
        >
          <div
            className="wz-root"
            style={{
              position: 'relative', background: '#fff', border: '1px solid var(--wz-slate100)',
              borderRadius: 24, maxWidth: 440, width: '100%', maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute', top: 14, right: 14, zIndex: 10, padding: 8,
                background: 'var(--wz-slate100)', borderRadius: 999, border: 'none',
                color: 'var(--wz-ink)', cursor: 'pointer', display: 'flex',
              }}
            >
              <X style={{ width: 18, height: 18 }} />
            </button>

            {memberLoading ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--wz-slate400)' }}>
                <div
                  style={{
                    display: 'inline-block', width: 44, height: 44, borderRadius: '50%',
                    border: '3px solid var(--wz-slate100)', borderBottomColor: 'var(--wz-violet)',
                    animation: 'szSpin 0.9s linear infinite',
                  }}
                />
                <p style={{ marginTop: 16 }}>Loading profile…</p>
              </div>
            ) : (
              <div style={{ padding: 28 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
                  <div
                    style={{
                      width: 120, height: 120, borderRadius: '50%', overflow: 'hidden',
                      border: '4px solid var(--wz-slate100)',
                    }}
                  >
                    <img
                      src={fullProfile?.profile_photo_url || selectedMember.profile_photo_url}
                      alt={fullProfile?.first_name || selectedMember.first_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                </div>

                <h2
                  className="wz-h3"
                  style={{
                    fontWeight: 700, fontSize: 24, color: 'var(--wz-ink)', textAlign: 'center',
                    margin: '0 0 6px',
                  }}
                >
                  {fullProfile?.first_name || selectedMember.first_name}
                </h2>

                {(fullProfile?.location_city || selectedMember.location) && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      color: 'var(--wz-slate400)', marginBottom: 22, fontSize: 14,
                    }}
                  >
                    <MapPin style={{ width: 16, height: 16 }} />
                    <span>
                      {fullProfile?.location_city
                        ? `${fullProfile.location_city}${
                            fullProfile.location_state ? `, ${fullProfile.location_state}` : ''
                          }`
                        : selectedMember.location}
                    </span>
                  </div>
                )}

                {(fullProfile?.bio || selectedMember.bio) && (
                  <p
                    style={{
                      color: 'var(--wz-slate700)', lineHeight: 1.6, textAlign: 'left',
                      marginBottom: 24, fontSize: 15,
                    }}
                  >
                    {fullProfile?.bio || selectedMember.bio}
                  </p>
                )}

                <button
                  onClick={() => navigate('/getquotes')}
                  className="wz-pill wz-pill-ink"
                  style={{ width: '100%', padding: '15px 24px', fontSize: 15 }}
                >
                  Get a quote
                  <ArrowRight style={{ width: 18, height: 18 }} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewDirectory;
